import { useState, useRef, useEffect, KeyboardEvent, useCallback } from 'react'
import { useBuildStore } from '../../stores/buildStore'
import { useBuildLogStore, type FeatureStatus, type FeatureCategory } from '../../stores/buildLogStore'
import { useWorkspaceStore } from '../../stores/workspaceStore'

interface TerminalLine {
  id: string
  type: 'input' | 'output' | 'error' | 'system'
  content: string
  timestamp: number
}

// For VPS deployment, we'll use the Synapse API instead of local WebSocket
const SYNAPSE_API = '/api/synapse'

export function Terminal() {
  const [lines, setLines] = useState<TerminalLine[]>([
    { id: '0', type: 'system', content: 'ControlDeck Terminal v1.0.0', timestamp: Date.now() },
    { id: '1', type: 'system', content: 'Connected to Synapse. Type "help" for commands.', timestamp: Date.now() },
  ])
  const [input, setInput] = useState('')
  const [history, setHistory] = useState<string[]>([])
  const [historyIndex, setHistoryIndex] = useState(-1)
  const [isExpanded, setIsExpanded] = useState(true)
  const [cwd, setCwd] = useState('/home/forge/mothershipio.ca/current')
  const [isRunning, setIsRunning] = useState(false)
  const [commandQueue, setCommandQueue] = useState<string[]>([])
  const [autoMode, setAutoMode] = useState(false)
  const autoModeRef = useRef(false)
  const [claudeMode, setClaudeMode] = useState(false)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; text: string } | null>(null)
  const [isConnected, setIsConnected] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const terminalRef = useRef<HTMLDivElement>(null)
  const build = useBuildStore((state) => state.build)
  const workspaces = useWorkspaceStore((state) => state.workspaces)
  const activeWorkspace = useWorkspaceStore((state) => state.activeWorkspace)

  // Generate contextual prompt based on current state
  const generatePrompt = useCallback(() => {
    const prompts: string[] = []
    const buildLog = useBuildLogStore.getState()
    const session = buildLog.getCurrentSession()
    const placeholders = session?.features.filter(f => f.status === 'placeholder') || []

    if (placeholders.length > 0) {
      const next = placeholders[0]
      prompts.push(`Implement ${next.name} - currently a placeholder in ${next.category}`)
    }

    if (build?.status === 'failed') {
      prompts.push(`Fix the failed build - check error logs and resolve issues`)
    }

    const workspace = activeWorkspace ? workspaces.get(activeWorkspace) : null
    if (workspace && workspace.tasks.length > 0) {
      const pending = workspace.tasks.filter(t => t.status === 'queued')
      if (pending.length > 0) {
        prompts.push(`Process queued task: ${pending[0].prompt.slice(0, 50)}...`)
      }
    }

    const recentErrors = lines.filter(l => l.type === 'error').slice(-3)
    if (recentErrors.length > 0) {
      prompts.push(`Debug recent error: ${recentErrors[recentErrors.length - 1].content}`)
    }

    const defaults = [
      'Add unit tests for the latest component',
      'Refactor to improve separation of concerns',
      'Add error handling and validation',
      'Optimize performance for large datasets',
      'Add accessibility improvements',
      'Document the public API'
    ]

    if (prompts.length > 0) {
      return prompts[Math.floor(Math.random() * prompts.length)]
    }
    return defaults[Math.floor(Math.random() * defaults.length)]
  }, [build, lines, workspaces, activeWorkspace])

  // Check Synapse connection
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const response = await fetch(`${SYNAPSE_API}/health`)
        setIsConnected(response.ok)
      } catch {
        setIsConnected(false)
      }
    }

    checkHealth()
    const interval = setInterval(checkHealth, 10000)
    return () => clearInterval(interval)
  }, [])

  // Auto-scroll to bottom
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [lines])

  // Auto-fire mode: process queued tasks
  useEffect(() => {
    autoModeRef.current = autoMode

    if (!autoMode) return

    const processNextTask = () => {
      if (!autoModeRef.current) return

      const wsStore = useWorkspaceStore.getState()
      const workspace = wsStore.activeWorkspace
        ? wsStore.workspaces.get(wsStore.activeWorkspace)
        : null

      if (!workspace) return

      const nextTask = workspace.tasks.find(t => t.status === 'queued')
      if (!nextTask) {
        addLine('system', '[AUTO] No more tasks in queue')
        setAutoMode(false)
        return
      }

      wsStore.updateTaskStatus(nextTask.id, 'running')
      addLine('system', `[AUTO] Processing: ${nextTask.prompt.slice(0, 50)}...`)

      // Submit to Synapse
      submitToSynapse(nextTask.prompt)
        .then(() => {
          wsStore.updateTaskStatus(nextTask.id, 'completed')
          if (autoModeRef.current) {
            setTimeout(processNextTask, 2000)
          }
        })
        .catch(() => {
          wsStore.updateTaskStatus(nextTask.id, 'queued')
        })
    }

    const timer = setTimeout(processNextTask, 1000)
    return () => clearTimeout(timer)
  }, [autoMode])

  const addLine = (type: TerminalLine['type'], content: string) => {
    setLines(prev => [...prev, {
      id: `${Date.now()}-${Math.random()}`,
      type,
      content,
      timestamp: Date.now()
    }])
  }

  const submitToSynapse = async (description: string): Promise<string> => {
    const response = await fetch(`${SYNAPSE_API}/tasks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': 'synapse-dev'
      },
      body: JSON.stringify({
        description,
        approve_opus: true
      })
    })

    if (!response.ok) throw new Error('Failed to submit task')
    const data = await response.json()
    return data.task_id
  }

  const executeCommand = async (cmd: string, fromQueue = false) => {
    const trimmed = cmd.trim()
    if (!trimmed) return

    // Queue command if one is already running
    if (isRunning && !fromQueue) {
      setCommandQueue(prev => [...prev, trimmed])
      addLine('system', `Queued: ${trimmed} (${commandQueue.length + 1} in queue)`)
      setHistory(prev => [...prev, trimmed])
      return
    }

    // Add to history
    if (!fromQueue) {
      setHistory(prev => [...prev, trimmed])
    }
    setHistoryIndex(-1)

    // Echo the command
    addLine('input', `${cwd.split('/').pop()}$ ${trimmed}`)

    // Parse command
    const [command, ...args] = trimmed.split(' ')

    // Handle built-in commands
    switch (command.toLowerCase()) {
      case 'help':
        addLine('output', 'ControlDeck Terminal - Connected to Synapse')
        addLine('output', '')
        addLine('output', 'Built-in commands:')
        addLine('output', '  clear         - Clear terminal')
        addLine('output', '  cd <dir>      - Change directory')
        addLine('output', '  status        - Show build status')
        addLine('output', '  agents        - List active agents')
        addLine('output', '  claude <task> - Submit task to Synapse')
        addLine('output', '')
        addLine('output', 'Task queue commands:')
        addLine('output', '  task <desc>   - Add task to queue')
        addLine('output', '  tasks         - List queued tasks')
        addLine('output', '  auto [on|off] - Auto-fire mode')
        addLine('output', '')
        addLine('output', 'Build log commands:')
        addLine('output', '  log <status> <category> <name> [files...]')
        addLine('output', '  build-log     - Show session features')
        addLine('output', '  export-log    - Copy to clipboard')
        return

      case 'clear':
        setLines([])
        return

      case 'cd':
        const newDir = args[0]
        if (!newDir) {
          setCwd('/home/forge')
        } else if (newDir.startsWith('/')) {
          setCwd(newDir)
        } else if (newDir === '..') {
          setCwd(cwd.split('/').slice(0, -1).join('/') || '/')
        } else {
          setCwd(`${cwd}/${newDir}`)
        }
        addLine('output', `Changed to: ${newDir || '/home/forge'}`)
        return

      case 'status':
        if (build) {
          addLine('output', `Build: ${build.id}`)
          addLine('output', `Status: ${build.status}`)
          addLine('output', `Wave: ${build.wave}`)
          addLine('output', `Progress: ${build.metrics?.completedTasks ?? 0}/${build.metrics?.totalTasks ?? 0} tasks`)
        } else {
          addLine('output', 'No active build')
        }
        return

      case 'agents':
        const agents = useBuildStore.getState().agents
        if (agents.size === 0) {
          addLine('output', 'No agents registered')
        } else {
          addLine('output', `${agents.size} agents:`)
          agents.forEach(agent => {
            const status = agent.status === 'busy' ? `working on ${agent.currentTask}` : agent.status
            addLine('output', `  ${agent.id} (${agent.model}) - ${status}`)
          })
        }
        return

      case 'claude':
      case 'ask': {
        const prompt = args.join(' ')
        if (!prompt) {
          addLine('error', 'Usage: claude <task description>')
          return
        }

        addLine('system', `Submitting to Synapse...`)
        setIsRunning(true)

        try {
          const taskId = await submitToSynapse(prompt)
          addLine('system', `Task submitted: ${taskId}`)
          addLine('output', 'Check task status in Claude tab or use task-status.sh')
        } catch (error) {
          addLine('error', 'Failed to submit task')
        } finally {
          setIsRunning(false)
        }
        return
      }

      case 'log': {
        if (args.length < 3) {
          addLine('error', 'Usage: log <implemented|placeholder> <category> <name> [files...]')
          addLine('output', 'Categories: ui, api, store, hook, util, config, server, other')
          return
        }
        const [statusArg, categoryArg, ...rest] = args
        const status = statusArg as FeatureStatus
        const category = categoryArg as FeatureCategory

        let name = ''
        let files: string[] = []
        const joined = rest.join(' ')

        if (joined.startsWith('"')) {
          const endQuote = joined.indexOf('"', 1)
          name = joined.slice(1, endQuote)
          files = joined.slice(endQuote + 2).split(' ').filter(Boolean)
        } else {
          name = rest[0]
          files = rest.slice(1)
        }

        if (!['implemented', 'placeholder'].includes(status)) {
          addLine('error', 'Status must be "implemented" or "placeholder"')
          return
        }

        const logStore = useBuildLogStore.getState()
        logStore.logFeature({
          name,
          description: `Added via terminal`,
          status,
          category,
          files
        })

        const emoji = status === 'implemented' ? '✓' : '○'
        addLine('system', `${emoji} Logged: ${name} [${status}] (${category})`)
        return
      }

      case 'build-log': {
        const logStore = useBuildLogStore.getState()
        const session = logStore.getCurrentSession()

        if (!session) {
          addLine('output', 'No active session')
          return
        }

        const implemented = session.features.filter(f => f.status === 'implemented')
        const placeholders = session.features.filter(f => f.status === 'placeholder')

        addLine('system', `Session: ${session.id}`)
        addLine('output', `Started: ${new Date(session.startedAt).toLocaleString()}`)
        addLine('output', '')
        addLine('output', `Implemented (${implemented.length}):`)
        implemented.forEach(f => addLine('output', `  ✓ ${f.name} [${f.category}]`))
        addLine('output', '')
        addLine('output', `Placeholders (${placeholders.length}):`)
        placeholders.forEach(f => addLine('output', `  ○ ${f.name} [${f.category}]`))
        return
      }

      case 'export-log': {
        const logStore = useBuildLogStore.getState()
        const markdown = logStore.exportSessionLog()

        if (!markdown) {
          addLine('error', 'No session to export')
          return
        }

        navigator.clipboard.writeText(markdown).then(() => {
          addLine('system', 'Build log copied to clipboard')
        }).catch(() => {
          addLine('output', markdown)
        })
        return
      }

      case 'task':
      case '/task': {
        const taskPrompt = args.join(' ')
        if (!taskPrompt) {
          addLine('error', 'Usage: task <description>')
          return
        }

        const wsStore = useWorkspaceStore.getState()
        let workspaceId = wsStore.activeWorkspace

        if (!workspaceId) {
          wsStore.addWorkspace('Default', cwd)
          workspaceId = wsStore.activeWorkspace
        }

        if (workspaceId) {
          wsStore.queueTask(workspaceId, taskPrompt)
          const workspace = wsStore.workspaces.get(workspaceId)
          addLine('system', `Task queued to ${workspace?.name || 'workspace'}`)
          addLine('output', `  "${taskPrompt}"`)
        }
        return
      }

      case 'tasks': {
        const wsStore = useWorkspaceStore.getState()
        const workspace = wsStore.activeWorkspace
          ? wsStore.workspaces.get(wsStore.activeWorkspace)
          : null

        if (!workspace || workspace.tasks.length === 0) {
          addLine('output', 'No tasks queued')
          return
        }

        addLine('system', `Tasks in ${workspace.name}:`)
        workspace.tasks.forEach((t, i) => {
          const icon = t.status === 'completed' ? '✓' : t.status === 'running' ? '▸' : '○'
          addLine('output', `  ${icon} [${i + 1}] ${t.prompt.slice(0, 50)}${t.prompt.length > 50 ? '...' : ''}`)
        })
        return
      }

      case 'auto': {
        const mode = args[0]?.toLowerCase()
        if (mode === 'on' || mode === 'start') {
          setAutoMode(true)
          addLine('system', '[AUTO] Mode ON - processing queue')
        } else if (mode === 'off' || mode === 'stop') {
          setAutoMode(false)
          addLine('system', '[AUTO] Mode OFF')
        } else {
          setAutoMode(!autoMode)
          addLine('system', `[AUTO] Mode ${!autoMode ? 'ON' : 'OFF'}`)
        }
        return
      }
    }

    // Claude mode: submit everything to Synapse
    if (claudeMode) {
      addLine('system', `[Claude] Submitting...`)
      setIsRunning(true)

      try {
        const taskId = await submitToSynapse(trimmed)
        addLine('system', `Task submitted: ${taskId}`)
      } catch (error) {
        addLine('error', 'Failed to submit to Synapse')
      } finally {
        setIsRunning(false)
      }
      return
    }

    // Unknown command
    addLine('error', `Unknown command: ${command}`)
    addLine('output', 'Type "help" for available commands')
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      executeCommand(input)
      setInput('')
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (history.length > 0) {
        const newIndex = historyIndex < history.length - 1 ? historyIndex + 1 : historyIndex
        setHistoryIndex(newIndex)
        setInput(history[history.length - 1 - newIndex] || '')
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1
        setHistoryIndex(newIndex)
        setInput(history[history.length - 1 - newIndex] || '')
      } else {
        setHistoryIndex(-1)
        setInput('')
      }
    } else if (e.key === 'Tab') {
      e.preventDefault()
      const commands = ['help', 'status', 'clear', 'agents', 'cd', 'claude', 'task', 'tasks', 'auto', 'log', 'build-log', 'export-log']
      const match = commands.find(c => c.startsWith(input.toLowerCase()))
      if (match) setInput(match)
    } else if (e.key === 'l' && e.ctrlKey) {
      e.preventDefault()
      setLines([])
    }
  }

  const focusInput = () => {
    inputRef.current?.focus()
  }

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const selection = window.getSelection()?.toString() || ''
    setContextMenu({ x: e.clientX, y: e.clientY, text: selection })
  }, [])

  const closeContextMenu = useCallback(() => {
    setContextMenu(null)
  }, [])

  const handleCopy = useCallback(() => {
    if (contextMenu?.text) {
      navigator.clipboard.writeText(contextMenu.text)
      addLine('system', 'Copied to clipboard')
    }
    closeContextMenu()
  }, [contextMenu])

  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText()
      setInput(prev => prev + text)
      inputRef.current?.focus()
    } catch {
      addLine('error', 'Failed to paste from clipboard')
    }
    closeContextMenu()
  }, [])

  const handleCopyAll = useCallback(() => {
    const allText = lines.map(l => l.content).join('\n')
    navigator.clipboard.writeText(allText)
    addLine('system', 'Copied all output to clipboard')
    closeContextMenu()
  }, [lines])

  const handleRunAsCommand = useCallback(() => {
    if (contextMenu?.text) {
      setInput(contextMenu.text.trim())
      inputRef.current?.focus()
    }
    closeContextMenu()
  }, [contextMenu])

  const handleAskClaude = useCallback(async () => {
    if (contextMenu?.text) {
      addLine('system', '[Claude] Submitting selection...')
      setIsRunning(true)
      try {
        const taskId = await submitToSynapse(contextMenu.text)
        addLine('system', `Task submitted: ${taskId}`)
      } catch {
        addLine('error', 'Failed to submit')
      } finally {
        setIsRunning(false)
      }
    }
    closeContextMenu()
  }, [contextMenu])

  // Close context menu on click outside
  useEffect(() => {
    if (!contextMenu) return
    const handleClick = () => closeContextMenu()
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [contextMenu, closeContextMenu])

  return (
    <div ref={terminalRef} className="h-full flex flex-col bg-[#0d1117] rounded-lg border border-[#30363d]">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-2 bg-[#161b22] border-b border-[#30363d] rounded-t-lg cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
          <span className="text-sm font-medium">Terminal</span>
          {autoMode && <span className="px-2 py-0.5 text-xs bg-yellow-600/30 text-yellow-400 rounded">AUTO</span>}
          {claudeMode && <span className="px-2 py-0.5 text-xs bg-purple-600/30 text-purple-400 rounded">CLAUDE</span>}
          <span className="text-xs text-gray-500">{cwd.split('/').slice(-2).join('/')}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="p-1 text-gray-500 hover:text-white transition-colors"
            onClick={(e) => { e.stopPropagation(); setLines([]) }}
            title="Clear"
          >
            ⌫
          </button>
          <button className="p-1 text-gray-500 hover:text-white transition-colors">
            {isExpanded ? '▼' : '▲'}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="flex-1 flex flex-col min-h-0" onClick={focusInput} onContextMenu={handleContextMenu}>
          {/* Output */}
          <div ref={containerRef} className="flex-1 overflow-y-auto p-4 font-mono text-sm">
            {lines.map(line => (
              <div key={line.id} className={`py-0.5 ${
                line.type === 'input' ? 'text-green-400' :
                line.type === 'error' ? 'text-red-400' :
                line.type === 'system' ? 'text-blue-400' :
                'text-gray-300'
              }`}>
                {line.content}
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="border-t border-[#30363d] p-2">
            <div className="flex items-center gap-2 bg-[#161b22] rounded-lg px-3 py-2">
              <button
                className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${
                  claudeMode ? 'bg-purple-600 text-white' : 'text-gray-500 hover:text-purple-400'
                }`}
                onClick={() => setClaudeMode(!claudeMode)}
                title={claudeMode ? 'Claude Mode ON' : 'Enable Claude Mode'}
              >
                ◆
              </button>
              <span className="text-green-400 font-mono text-sm">{cwd.split('/').pop()}$</span>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 bg-transparent text-white font-mono text-sm outline-none"
                placeholder="Type a command..."
                spellCheck={false}
                autoComplete="off"
                autoFocus
              />
              <button
                className="p-1 text-gray-500 hover:text-cyan-400 transition-colors"
                onClick={() => setInput(generatePrompt())}
                title="Generate prompt"
              >
                ✨
              </button>
              <div className="text-gray-500 text-xs">
                {isRunning ? (
                  <span className="flex items-center gap-1">
                    ⏳{commandQueue.length > 0 && <span>+{commandQueue.length}</span>}
                  </span>
                ) : <kbd className="px-1 py-0.5 bg-[#21262d] rounded">↵</kbd>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-[#161b22] border border-[#30363d] rounded-lg shadow-xl py-1 z-50 min-w-[160px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.text && (
            <>
              <button className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-[#21262d] flex items-center gap-2" onClick={handleCopy}>
                📋 Copy
              </button>
              <button className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-[#21262d] flex items-center gap-2" onClick={handleRunAsCommand}>
                ▸ Run as Command
              </button>
              <button className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-[#21262d] flex items-center gap-2" onClick={handleAskClaude}>
                ◆ Ask Claude
              </button>
              <div className="border-t border-[#30363d] my-1" />
            </>
          )}
          <button className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-[#21262d] flex items-center gap-2" onClick={handlePaste}>
            📥 Paste
          </button>
          <button className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-[#21262d] flex items-center gap-2" onClick={handleCopyAll}>
            📄 Copy All Output
          </button>
          <div className="border-t border-[#30363d] my-1" />
          <button className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-[#21262d] flex items-center gap-2" onClick={() => { setLines([]); closeContextMenu() }}>
            🗑 Clear Terminal
          </button>
        </div>
      )}
    </div>
  )
}
